// src/components/Dashboard.js - UPDATED: REMOVED ACTIVE MONITORS LIST

import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FaPlay, FaSync, FaChartLine, FaBell, FaStop, FaEye, FaEyeSlash, FaClock, FaCog } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axios from 'axios';
import ChartComponent from './ChartComponent';

const API_BASE_URL = 'https://gritwears.store/api';

// Keys for localStorage
const STORAGE_KEYS = {
  CHART_SYMBOL: 'tradehawk_chart_symbol',
  CHART_INTERVAL: 'tradehawk_chart_interval',
  CHART_CATEGORY: 'tradehawk_chart_category',
  SELECTED_SYMBOL: 'tradehawk_selected_symbol',
  SELECTED_TIMEFRAMES: 'tradehawk_selected_timeframes',
  DIRECTION: 'tradehawk_direction',
  ENABLED_PATTERNS: 'tradehawk_enabled_patterns',
  WATCH_ALL_PATTERNS: 'tradehawk_watch_all_patterns',
};

const Dashboard = ({ stats, socket, onMonitorStart }) => {
  const { user } = useSelector((state) => state.auth);
  
  const [symbols, setSymbols] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedTimeframes, setSelectedTimeframes] = useState(new Set());
  const [direction, setDirection] = useState('both');
  const [timeframes, setTimeframes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState('60');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(0);
  const [watchAllPatterns, setWatchAllPatterns] = useState(true);
  const [enabledPatterns, setEnabledPatterns] = useState({
    choch: true,
    cisd: true,
    bos: true
  });
  const [showPatternSelector, setShowPatternSelector] = useState(false);
  const [chartCategory, setChartCategory] = useState('all');
  const [chartSymbols, setChartSymbols] = useState([]);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const chartRef = useRef(null);
  const priceUpdateInterval = useRef(null);

  // ============================================
  // LOCAL STORAGE HELPERS
  // ============================================

  const saveToStorage = (key, value) => {
    try {
      if (value instanceof Set) {
        localStorage.setItem(key, JSON.stringify([...value]));
      } else if (typeof value === 'object') {
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        localStorage.setItem(key, String(value));
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const getFromStorage = (key, defaultValue) => {
    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;
      
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue;
    }
  };

  // ============================================
  // LOAD FUNCTIONS
  // ============================================

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/symbols/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadTimeframes = async () => {
    try {
      let response = await axios.get(`${API_BASE_URL}/market-data/timeframes`);
      setTimeframes(response.data);
    } catch (error) {
      try {
        const fallbackResponse = await axios.get(`${API_BASE_URL}/timeframes`);
        setTimeframes(fallbackResponse.data);
      } catch (fallbackError) {
        console.error('Error loading timeframes:', fallbackError);
        setTimeframes([
          { value: "60", label: "1 Minute" },
          { value: "300", label: "5 Minutes" },
          { value: "900", label: "15 Minutes" },
          { value: "1800", label: "30 Minutes" },
          { value: "3600", label: "1 Hour" },
          { value: "14400", label: "4 Hours" },
          { value: "86400", label: "1 Day" },
        ]);
      }
    }
  };

  const loadSymbols = async (category) => {
    try {
      let url = `${API_BASE_URL}/symbols/real`;
      if (category && category !== 'all') {
        url = `${API_BASE_URL}/symbols/real?category=${encodeURIComponent(category)}`;
      }
      const response = await axios.get(url);
      setSymbols(response.data);
      
      const savedSymbol = getFromStorage(STORAGE_KEYS.SELECTED_SYMBOL, '');
      if (savedSymbol && response.data.some(s => s.value === savedSymbol)) {
        setSelectedSymbol(savedSymbol);
      } else if (response.data.length > 0 && !selectedSymbol) {
        setSelectedSymbol(response.data[0].value);
      }
    } catch (error) {
      console.error('Error loading symbols:', error);
      toast.error('Failed to load symbols');
    }
  };

  const loadChartSymbols = async (category) => {
    try {
      let url = `${API_BASE_URL}/symbols/real`;
      if (category && category !== 'all') {
        url = `${API_BASE_URL}/symbols/real?category=${encodeURIComponent(category)}`;
      }
      const response = await axios.get(url);
      setChartSymbols(response.data);
      
      const savedChartSymbol = getFromStorage(STORAGE_KEYS.CHART_SYMBOL, '');
      if (savedChartSymbol && response.data.some(s => s.value === savedChartSymbol)) {
        const currentExists = response.data.some(s => s.value === selectedSymbol);
        if (!currentExists) {
          setSelectedSymbol(savedChartSymbol);
        }
      } else if (response.data.length > 0) {
        const currentExists = response.data.some(s => s.value === selectedSymbol);
        if (!currentExists && selectedSymbol) {
          setSelectedSymbol(response.data[0].value);
        }
      }
    } catch (error) {
      console.error('Error loading chart symbols:', error);
    }
  };

  // ============================================
  // PRICE FETCHING
  // ============================================

  const fetchCurrentPrice = async () => {
    if (!user?.uid || !selectedSymbol) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/market-data/tick/${user.uid}/${encodeURIComponent(selectedSymbol)}`
      );
      
      if (response.data.success && response.data.price) {
        const price = response.data.price;
        setCurrentPrice(price);
        if (lastUpdateTime) {
          setLastUpdateTime(new Date());
        }
      }
    } catch (error) {
      // Silently fail
    }
  };

  // ============================================
  // HANDLERS
  // ============================================

  const handleCategoryChange = async (category) => {
    setSelectedCategory(category);
    saveToStorage(STORAGE_KEYS.CHART_CATEGORY, category);
    await loadSymbols(category);
  };

  const handleChartCategoryChange = async (category) => {
    setChartCategory(category);
    saveToStorage(STORAGE_KEYS.CHART_CATEGORY, category);
    await loadChartSymbols(category);
  };

  const toggleTimeframe = (tf) => {
    const newSet = new Set(selectedTimeframes);
    if (newSet.has(tf)) newSet.delete(tf);
    else newSet.add(tf);
    setSelectedTimeframes(newSet);
    saveToStorage(STORAGE_KEYS.SELECTED_TIMEFRAMES, newSet);
  };

  const handleWatchAllToggle = () => {
    const newWatchAll = !watchAllPatterns;
    setWatchAllPatterns(newWatchAll);
    saveToStorage(STORAGE_KEYS.WATCH_ALL_PATTERNS, newWatchAll);
    if (newWatchAll) {
      const newPatterns = { choch: true, cisd: true, bos: true };
      setEnabledPatterns(newPatterns);
      saveToStorage(STORAGE_KEYS.ENABLED_PATTERNS, newPatterns);
    }
  };

  const handlePatternToggle = (pattern) => {
    const newPatterns = { ...enabledPatterns };
    newPatterns[pattern] = !newPatterns[pattern];
    setEnabledPatterns(newPatterns);
    saveToStorage(STORAGE_KEYS.ENABLED_PATTERNS, newPatterns);
    
    if (watchAllPatterns && !newPatterns[pattern]) {
      setWatchAllPatterns(false);
      saveToStorage(STORAGE_KEYS.WATCH_ALL_PATTERNS, false);
    }
    
    if (newPatterns.choch && newPatterns.cisd && newPatterns.bos) {
      setWatchAllPatterns(true);
      saveToStorage(STORAGE_KEYS.WATCH_ALL_PATTERNS, true);
    }
  };

  const handleSymbolChange = (symbol) => {
    setSelectedSymbol(symbol);
    saveToStorage(STORAGE_KEYS.SELECTED_SYMBOL, symbol);
    saveToStorage(STORAGE_KEYS.CHART_SYMBOL, symbol);
    setCurrentPrice(null);
    setLastUpdateTime(null);
  };

  const handleIntervalChange = (interval) => {
    setSelectedInterval(interval);
    saveToStorage(STORAGE_KEYS.CHART_INTERVAL, interval);
    setCurrentPrice(null);
    setLastUpdateTime(null);
  };

  const handleDirectionChange = (dir) => {
    setDirection(dir);
    saveToStorage(STORAGE_KEYS.DIRECTION, dir);
  };

  const handleStartMonitoring = async () => {
    if (!user?.uid) {
      toast.error('User not authenticated. Please login again.');
      return;
    }
    if (!selectedSymbol) {
      toast.error('Please select a symbol');
      return;
    }
    if (selectedTimeframes.size === 0) {
      toast.error('Please select at least one timeframe');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/start`, {
        userId: user.uid,
        symbol: selectedSymbol,
        intervals: Array.from(selectedTimeframes),
        direction,
        enabledPatterns
      });
      if (response.data.success) {
        toast.success(`Monitoring started for ${selectedSymbol}!`);
        if (onMonitorStart) onMonitorStart();
        setSelectedTimeframes(new Set());
        saveToStorage(STORAGE_KEYS.SELECTED_TIMEFRAMES, new Set());
      } else {
        toast.error(response.data.message || 'Failed to start monitoring');
      }
    } catch (error) {
      console.error('Error starting monitoring:', error);
      toast.error(error.response?.data?.message || 'Error starting monitoring');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceUpdate = (price, change) => {
    setCurrentPrice(price);
    setPriceChange(change);
    setLastUpdateTime(new Date());
  };

  const formatTimeframeLabel = (seconds) => {
    const map = { 
      60: '1m', 120: '2m', 180: '3m', 300: '5m', 600: '10m', 
      900: '15m', 1800: '30m', 3600: '1h', 7200: '2h', 
      14400: '4h', 28800: '8h', 43200: '12h', 86400: '1d', 604800: '1w' 
    };
    return map[seconds] || `${seconds}s`;
  };

  const getSymbolCategory = () => {
    const symbol = symbols.find(s => s.value === selectedSymbol);
    return symbol?.category || '';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Volatility': '#f59e0b',
      'Boom & Crash': '#ef4444',
      'Forex Majors': '#3b82f6',
      'Crypto': '#8b5cf6',
      'Commodities': '#10b981',
      'Other': '#6b7280'
    };
    return colors[category] || '#6b7280';
  };

  // ============================================
  // INITIALIZATION - Load saved state
  // ============================================

  useEffect(() => {
    const savedSymbol = getFromStorage(STORAGE_KEYS.SELECTED_SYMBOL, '');
    const savedInterval = getFromStorage(STORAGE_KEYS.CHART_INTERVAL, '60');
    const savedCategory = getFromStorage(STORAGE_KEYS.CHART_CATEGORY, 'all');
    const savedDirection = getFromStorage(STORAGE_KEYS.DIRECTION, 'both');
    const savedWatchAll = getFromStorage(STORAGE_KEYS.WATCH_ALL_PATTERNS, true);
    const savedPatterns = getFromStorage(STORAGE_KEYS.ENABLED_PATTERNS, { choch: true, cisd: true, bos: true });
    const savedTimeframes = getFromStorage(STORAGE_KEYS.SELECTED_TIMEFRAMES, []);
    
    if (savedSymbol) setSelectedSymbol(savedSymbol);
    if (savedInterval) setSelectedInterval(savedInterval);
    if (savedCategory) setChartCategory(savedCategory);
    if (savedDirection) setDirection(savedDirection);
    if (savedWatchAll !== undefined) setWatchAllPatterns(savedWatchAll);
    if (savedPatterns) setEnabledPatterns(savedPatterns);
    if (savedTimeframes.length > 0) setSelectedTimeframes(new Set(savedTimeframes));
    
    loadCategories();
    loadTimeframes();
    loadSymbols('all');
    loadChartSymbols('all');
    
    setIsInitialLoad(false);
    
    if (socket) {
      socket.on('signal', (signal) => {
        toast.success(`${signal.type} signal detected for ${signal.symbol}`);
      });
      
      socket.on('price_update', (data) => {
        if (data.symbol === selectedSymbol) {
          setCurrentPrice(data.price);
          if (lastUpdateTime) {
            setLastUpdateTime(new Date());
          }
        }
      });
    }
    
    priceUpdateInterval.current = setInterval(() => {
      if (selectedSymbol && user?.uid) {
        fetchCurrentPrice();
      }
    }, 1000);
    
    return () => {
      if (socket) {
        socket.off('signal');
        socket.off('price_update');
      }
      if (priceUpdateInterval.current) {
        clearInterval(priceUpdateInterval.current);
      }
    };
  }, []);

  // Save to localStorage whenever selected values change
  useEffect(() => {
    if (!isInitialLoad) {
      saveToStorage(STORAGE_KEYS.CHART_SYMBOL, selectedSymbol);
    }
  }, [selectedSymbol]);

  useEffect(() => {
    if (!isInitialLoad) {
      saveToStorage(STORAGE_KEYS.CHART_INTERVAL, selectedInterval);
    }
  }, [selectedInterval]);

  useEffect(() => {
    if (!isInitialLoad) {
      saveToStorage(STORAGE_KEYS.DIRECTION, direction);
    }
  }, [direction]);

  return (
    <div className="dashboard-container">
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-info">
            <h3>{stats?.activeCount || 0}</h3>
            <p>Active Monitors</p>
          </div>
          <div className="stat-icon"><FaChartLine /></div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3>{stats?.signalCount || 0}</h3>
            <p>Total Signals</p>
          </div>
          <div className="stat-icon"><FaBell /></div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3>{Math.floor(stats?.uptime || 0)}s</h3>
            <p>System Uptime</p>
          </div>
          <div className="stat-icon"><FaSync /></div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3>{stats?.lastSignal || 'N/A'}</h3>
            <p>Last Signal</p>
          </div>
          <div className="stat-icon"><FaChartLine /></div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="dashboard-main">
        {/* Left Panel - Trading Configuration */}
        <div className="panel-left">
          <div className="card">
            <div className="card-header">
              <h2>🎯 Trading Configuration</h2>
            </div>
            
            <div className="category-filters">
              <button 
                className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`} 
                onClick={() => handleCategoryChange('all')}
              >
                All
              </button>
              {categories.map(cat => (
                <button 
                  key={cat} 
                  className={`category-btn ${selectedCategory === cat ? 'active' : ''}`} 
                  onClick={() => handleCategoryChange(cat)}
                  style={{ 
                    borderColor: selectedCategory === cat ? getCategoryColor(cat) : 'transparent',
                    backgroundColor: selectedCategory === cat ? `${getCategoryColor(cat)}20` : 'transparent'
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="form-group">
              <label>Symbol/Pair:</label>
              <select 
                value={selectedSymbol} 
                onChange={(e) => handleSymbolChange(e.target.value)}
              >
                {symbols.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Timeframes (Select Multiple):</label>
              <div className="timeframe-group">
                {timeframes.map(tf => (
                  <div 
                    key={tf.value} 
                    className={`timeframe-checkbox ${selectedTimeframes.has(tf.value) ? 'selected' : ''}`}
                    onClick={() => toggleTimeframe(tf.value)}
                  >
                    {formatTimeframeLabel(parseInt(tf.value))}
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Alert Direction:</label>
              <select 
                value={direction} 
                onChange={(e) => handleDirectionChange(e.target.value)}
              >
                <option value="both">🔄 Both Buy & Sell</option>
                <option value="bullish">🟢 Buy Only</option>
                <option value="bearish">🔴 Sell Only</option>
              </select>
            </div>

            <div className="form-group">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowPatternSelector(!showPatternSelector)}
                style={{ width: '100%', marginBottom: '10px' }}
              >
                {showPatternSelector ? 'Hide' : 'Show'} Pattern Settings
              </button>
              
              {showPatternSelector && (
                <div className="pattern-selection">
                  <div className="watch-all-toggle">
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={watchAllPatterns} 
                        onChange={handleWatchAllToggle} 
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">
                      {watchAllPatterns ? '👁️ Watching All Patterns' : 'Select Patterns Manually'}
                    </span>
                  </div>
                  
                  <div className={`pattern-checkboxes ${watchAllPatterns ? 'disabled' : ''}`}>
                    <label className={`pattern-checkbox ${enabledPatterns.choch ? 'selected' : ''}`}>
                      <input 
                        type="checkbox" 
                        checked={enabledPatterns.choch} 
                        onChange={() => handlePatternToggle('choch')}
                        disabled={watchAllPatterns}
                      />
                      <span>🔄 CHoCH</span>
                    </label>
                    <label className={`pattern-checkbox ${enabledPatterns.cisd ? 'selected' : ''}`}>
                      <input 
                        type="checkbox" 
                        checked={enabledPatterns.cisd} 
                        onChange={() => handlePatternToggle('cisd')}
                        disabled={watchAllPatterns}
                      />
                      <span>⚡ CISD</span>
                    </label>
                    <label className={`pattern-checkbox ${enabledPatterns.bos ? 'selected' : ''}`}>
                      <input 
                        type="checkbox" 
                        checked={enabledPatterns.bos} 
                        onChange={() => handlePatternToggle('bos')}
                        disabled={watchAllPatterns}
                      />
                      <span>📊 BOS</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <button 
              onClick={handleStartMonitoring} 
              className="btn btn-success" 
              disabled={loading}
            >
              <FaPlay /> {loading ? 'Starting...' : 'Start Monitoring'}
            </button>
          </div>
        </div>

        {/* Right Panel - Chart */}
        <div className="panel-right">
          <div className="card chart-card">
            <div className="card-header">
              <h2>📈 Live Price Chart</h2>
              <div className="chart-controls">
                {/* Category filter for chart */}
                <div className="chart-category-filters">
                  <button 
                    className={`chart-category-btn ${chartCategory === 'all' ? 'active' : ''}`} 
                    onClick={() => handleChartCategoryChange('all')}
                  >
                    All
                  </button>
                  {categories.map(cat => {
                    const color = getCategoryColor(cat);
                    return (
                      <button 
                        key={cat} 
                        className={`chart-category-btn ${chartCategory === cat ? 'active' : ''}`} 
                        onClick={() => handleChartCategoryChange(cat)}
                        style={{
                          borderColor: chartCategory === cat ? color : 'transparent',
                          backgroundColor: chartCategory === cat ? `${color}15` : 'transparent',
                          color: chartCategory === cat ? color : 'inherit',
                          boxShadow: chartCategory === cat ? `0 0 20px ${color}25` : 'none',
                        }}
                      >
                        <span className="category-dot" style={{ backgroundColor: color }}></span>
                        {cat}
                      </button>
                    );
                  })}
                </div>
                
                <div className="chart-selectors">
                  <select 
                    value={selectedSymbol} 
                    onChange={(e) => handleSymbolChange(e.target.value)}
                    className="chart-symbol-select"
                  >
                    {chartSymbols.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <select 
                    value={selectedInterval} 
                    onChange={(e) => handleIntervalChange(e.target.value)}
                    className="chart-interval-select"
                  >
                    {timeframes.map(tf => (
                      <option key={tf.value} value={tf.value}>{tf.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Price Display - Live Updates */}
            <div className="price-display">
              <div className="price-main">
                <span className="price-value">
                  {currentPrice ? currentPrice.toFixed(5) : '—'}
                </span>
                <span className={`price-change ${priceChange >= 0 ? 'positive' : 'negative'}`}>
                  {priceChange !== 0 && (
                    <>
                      {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                    </>
                  )}
                </span>
              </div>
              <div className="price-meta">
                {getSymbolCategory() && (
                  <span className="price-category" style={{ 
                    backgroundColor: `${getCategoryColor(getSymbolCategory())}20`,
                    color: getCategoryColor(getSymbolCategory())
                  }}>
                    {getSymbolCategory()}
                  </span>
                )}
                {lastUpdateTime && (
                  <span className="price-time">
                    <FaClock size={10} style={{ marginRight: '4px' }} />
                    {lastUpdateTime.toLocaleTimeString()}
                  </span>
                )}
                <span className="price-symbol">{selectedSymbol}</span>
              </div>
            </div>
            
            <div className="chart-wrapper">
              <ChartComponent 
                ref={chartRef}
                symbol={selectedSymbol}
                interval={selectedInterval}
                onPriceUpdate={handlePriceUpdate}
                category={getSymbolCategory()}
                socket={socket}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;